/**
 * SurveyCapture — Main capture screen: camera, voice, notes, area selector.
 * Route: /visits/:visitId/survey/capture
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ImageIcon } from 'lucide-react'
import { AreaSelector } from '../../components/survey/AreaSelector'
import { PhotoCapture } from '../../components/survey/PhotoCapture'
import { VoiceRecorder } from '../../components/survey/VoiceRecorder'
import { QuickFinding } from '../../components/survey/QuickFinding'
import { SyncStatus } from '../../components/survey/SyncStatus'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { surveyStore, type PendingPhoto } from '../../lib/offline/surveyStore'
import type { FacilityArea, SurveySession } from '../../lib/services/survey'

export function SurveyCapture() {
  const { visitId } = useParams<{ visitId: string }>()
  const [area, setArea] = useState<FacilityArea | null>(null)
  const [session, setSession] = useState<SurveySession | null>(null)
  const [recentPhotos, setRecentPhotos] = useState<PendingPhoto[]>([])

  useEffect(() => {
    if (!visitId) return
    surveyStore.getSessionByVisit(visitId).then((s) => setSession(s ?? null))
  }, [visitId])

  useEffect(() => {
    if (!session) return
    surveyStore.getPhotosBySurvey(session.survey_id).then(setRecentPhotos)
  }, [session])

  if (!visitId || !session) {
    return (
      <FieldAppShell eyebrow="Survey" title="Capture" backTo=".." backLabel="Back to Dashboard">
        <div className="px-4 py-6">
          <p className="text-sm text-slate-500">No active survey. Start one from the dashboard.</p>
          <Link to=".." className="btn-ghost text-sm mt-4 inline-block">Back to Dashboard</Link>
        </div>
      </FieldAppShell>
    )
  }

  return (
    <FieldAppShell eyebrow="Survey" title="Capture" backTo=".." backLabel="Back to Dashboard" rightSlot={<SyncStatus />}>
    <div className="space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to=".." className="text-slate-400 hover:text-slate-600">
            <ArrowLeft size={20} />
          </Link>
          <h2 className="section-title">Capture</h2>
        </div>
        <SyncStatus />
      </div>

      {/* Area selector */}
      <div className="app-surface p-4 space-y-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Current Area</span>
        <AreaSelector value={area} onChange={setArea} />
      </div>

      {/* Photo + Voice capture */}
      {area && (
        <div className="app-surface p-4 space-y-3">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Capture in {area}
          </span>
          <div className="flex items-center gap-2">
            <PhotoCapture
              visitId={visitId}
              surveyId={session.survey_id}
              area={area}
              onCapture={(photo) => setRecentPhotos((prev) => [photo, ...prev])}
            />
            <VoiceRecorder
              captureAudio
              onTranscript={() => {
                // Transcript will be used by QuickFinding below
              }}
            />
          </div>
        </div>
      )}

      {/* Quick finding entry */}
      <QuickFinding
        visitId={visitId}
        surveyId={session.survey_id}
        defaultArea={area}
      />

      {/* Recent photos */}
      {recentPhotos.length > 0 && (
        <div className="app-surface p-4 space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Recent Photos ({recentPhotos.length})
          </span>
          <div className="grid grid-cols-4 gap-2">
            {recentPhotos.slice(0, 8).map((p) => (
              <div
                key={p.photo_id}
                className="aspect-square rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200 overflow-hidden"
              >
                {p.blob ? (
                  <img
                    src={URL.createObjectURL(p.blob)}
                    alt={p.area}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={20} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </FieldAppShell>
  )
}
